import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  Heart,
  Star,
  MapPin,
  Wifi,
  Wind,
  Utensils,
  Compass,
  Sparkles,
  ArrowRight,
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import "./HandpickedRomanticStays.css";

const API_ENDPOINT = "https://admin.jayrajgroup.com/api/hotels";
const SKELETON_COUNT = 3;

const CATEGORY_TAGS = [
  "Overwater Villa",
  "Private Beach Resort",
  "Mountain Luxury Resort",
  "Heritage Palace Hotel",
];

/* ---------- helpers ---------- */
const createSlug = (title = "") =>
  title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const cleanText = (html) => {
  if (!html)
    return "Experience unmatched luxury and romantic surroundings tailored for unforgettable moments.";
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const text = doc.body.textContent || "";
    return text.length > 130 ? `${text.substring(0, 130)}…` : text;
  } catch {
    return html;
  }
};

const getMinPrice = (rooms) => {
  if (!rooms || rooms.length === 0) return 3500;
  const prices = rooms
    .map((r) => parseFloat(r.price_per_night))
    .filter(Boolean);
  return prices.length > 0 ? Math.min(...prices) : 3500;
};

const formatINR = (value) => {
  const num = Number(value);
  if (!num || Number.isNaN(num)) return null;
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(num);
  } catch {
    return `₹${num}`;
  }
};

const renderAmenityIcon = (name) => {
  const n = (name || "").toLowerCase();
  if (n.includes("wifi")) return <Wifi size={12} />;
  if (n.includes("airconditioner") || n.includes("ac")) return <Wind size={12} />;
  if (n.includes("restaurant") || n.includes("food")) return <Utensils size={12} />;
  if (n.includes("sightseeing")) return <Compass size={12} />;
  return <ShieldCheck size={12} />;
};

/* ---------- component ---------- */
const HandpickedRomanticStays = () => {
  const [stays, setStays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wishlist, setWishlist] = useState({});
  const sliderRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const abortController = new AbortController();

    const fetchHotels = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(API_ENDPOINT, {
          signal: abortController.signal,
        });
        const result = await response.json();
        if (result.status && Array.isArray(result.data)) {
          setStays(result.data);
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          setError(err.message);
          console.error("Error fetching romantic stays:", err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchHotels();
    return () => abortController.abort();
  }, []);

  const toggleWishlist = (e, id) => {
    e.stopPropagation();
    setWishlist((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleClick = (resort) => {
    const slug = createSlug(resort?.slug || resort?.name || "resort");
    const id = resort?.id;
    const random = Math.floor(100 + Math.random() * 900);
    const encodedId = id + random;
    navigate(`/hotel/${slug}-${encodedId}-${random}`);
  };

  const scrollSlider = (direction) => {
    if (sliderRef.current) {
      const amount = direction === "left" ? -380 : 380;
      sliderRef.current.scrollBy({ left: amount, behavior: "smooth" });
    }
  };

  return (
    <section className="rss-section">
      <span className="rss-orb rss-orb-a" aria-hidden="true" />
      <span className="rss-orb rss-orb-b" aria-hidden="true" />

      <div className="rss-shell">
        {/* ---------- Header ---------- */}
        <motion.header
          className="rss-header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
        >
          <div className="rss-header-text">
            <h2 className="rss-title">
              Stay Somewhere as Beautiful as Your Love Story
            </h2>
            <p className="rss-subtitle">
              Handpicked luxury sanctuaries designed for intimacy, comfort, and
              timeless romantic memories.
            </p>
          </div>

          <div className="rss-controls">
            <button
              type="button"
              className="rss-ctrl"
              onClick={() => scrollSlider("left")}
              aria-label="Previous stays"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              className="rss-ctrl"
              onClick={() => scrollSlider("right")}
              aria-label="Next stays"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </motion.header>

        {/* ---------- Body ---------- */}
        {error ? (
          <div className="rss-empty rss-empty-error">
            <span className="rss-empty-icon">
              <AlertCircle />
            </span>
            <h3 className="rss-empty-title">Couldn’t load stays</h3>
            <p className="rss-empty-text">Please try again in a moment.</p>
          </div>
        ) : loading ? (
          <div className="rss-rail" aria-busy="true" aria-label="Loading stays">
            {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
              <div key={i} className="rss-skeleton" />
            ))}
          </div>
        ) : stays.length === 0 ? (
          <div className="rss-empty">
            <span className="rss-empty-icon">
              <AlertCircle />
            </span>
            <h3 className="rss-empty-title">No stays yet</h3>
            <p className="rss-empty-text">New sanctuaries are being added soon.</p>
          </div>
        ) : (
          <div className="rss-rail" ref={sliderRef}>
            {stays.map((stay, index) => {
              const isLiked = !!wishlist[stay.id];
              const minPrice = getMinPrice(stay.rooms);
              const rating =
                stay.average_rating > 0 ? stay.average_rating : 4.9;
              const category =
                CATEGORY_TAGS[index % CATEGORY_TAGS.length] || "Luxury Stay";

              return (
                <motion.article
                  key={stay.id}
                  className="rss-card"
                  onClick={() => handleClick(stay)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleClick(stay);
                    }
                  }}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{
                    duration: 0.5,
                    delay: Math.min(index, 6) * 0.08,
                  }}
                >
                  {/* ---------- Media ---------- */}
                  <div className="rss-media">
                    <img
                      src={stay.featured_image || stay.featured_images?.[0]}
                      alt={stay.name}
                      className="rss-img"
                      loading="lazy"
                      onError={(e) => {
                        e.target.src =
                          "https://via.placeholder.com/800x600?text=No+Image";
                      }}
                    />
                    <span className="rss-veil" aria-hidden="true" />

                    <span className="rss-badge">{category}</span>
                  </div>

                  {/* ---------- Body ---------- */}
                  <div className="rss-body">
                    <div className="rss-meta">
                      <span className="rss-meta-loc">
                        <MapPin size={12} aria-hidden="true" />
                        {stay.location}
                      </span>
                      <span className="rss-meta-rating">
                        <Star size={12} fill="currentColor" aria-hidden="true" />
                        {rating}
                      </span>
                    </div>

                    <h3 className="rss-card-title">{stay.name}</h3>

                    <p className="rss-card-desc">
                      {cleanText(stay.description)}
                    </p>

                    <div className="rss-amenities">
                      {stay.amenities && stay.amenities.length > 0 ? (
                        stay.amenities.slice(0, 4).map((item) => (
                          <span
                            key={item.id}
                            className="rss-amenity"
                            title={item.name}
                          >
                            {renderAmenityIcon(item.name)}
                            <span>{item.name}</span>
                          </span>
                        ))
                      ) : (
                        <span className="rss-amenity">
                          <ShieldCheck size={12} />
                          <span>Premium Services</span>
                        </span>
                      )}
                    </div>

                    <div className="rss-footer">
                      <div className="rss-price">
                        <span className="rss-price-caption">
                          Starting from
                        </span>
                        <div className="rss-price-value">
                          {formatINR(minPrice) || "—"}
                          <span className="rss-price-unit">/ night</span>
                        </div>
                      </div>

                      <span className="rss-cta">
                        View Stay
                        <ArrowRight size={14} aria-hidden="true" />
                      </span>
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default HandpickedRomanticStays;